import * as THREE from 'three';
import { buildWeaponModel, ADS_BY_MODEL } from './models/index.js';

/**
 * First-person weapon animation.
 *
 * This is the layer the player stares at for the entire game, so almost everything
 * here exists to add *secondary motion* — the movement a real object makes because
 * it has mass and is attached to a body that is also moving.
 *
 * Structure. Transforms are stacked as separate groups rather than summed into one,
 * because each layer needs a different pivot and that is not something you can fake
 * by adding offsets:
 *
 *   root      pose blend + sway + bob        pivot: the shooter's wrist
 *    └ kick   recoil impulse                 pivot: the buttstock, so a kick rotates
 *       └ hold  model scale + reload/inspect        the muzzle up rather than
 *          └ model                                  sliding the whole gun up
 *
 * ADS is solved, not eyeballed. Each model exposes an `aimPoint` — the optical centre
 * of its sight in model space — and the ADS pose is derived so that point lands
 * exactly on the camera axis at the weapon's eye-relief distance. That guarantees the
 * sight picture is centred on the crosshair for every weapon, including ones added
 * later, which hand-tuned offsets never manage.
 */

export const TUNING = {
  // Models are authored in real metres. A viewmodel is then scaled down slightly and
  // pushed away from the eye — at true 1:1 with a 60-degree view FOV the weapon eats
  // nearly half the frame, which reads as "toy held at arm's length" rather than
  // "rifle shouldered". 0.86 lands it at roughly a third of the frame, which is where
  // every modern shooter sits.
  modelScale: 0.86,

  // --- resting poses, in view space (metres, radians) ---
  poses: {
    hip: { pos: [0.094, -0.076, -0.358], rot: [0.026, -0.076, 0.042] },
    crouch: { pos: [0.088, -0.066, -0.350], rot: [0.020, -0.068, 0.038] },
    sprint: { pos: [0.134, -0.140, -0.318], rot: [-0.30, -0.46, 0.46] },
    tacSprint: { pos: [0.086, -0.172, -0.258], rot: [-0.64, -1.00, 1.00] },
    slide: { pos: [0.136, -0.116, -0.322], rot: [-0.14, -0.38, 0.36] },
    air: { pos: [0.120, -0.118, -0.348], rot: [0.012, -0.102, 0.060] },
  },
  poseLerp: 9.5,

  // --- sway: the weapon lags the camera and springs back ---
  swayRotAmount: 0.055, // radians per rad/s of look speed
  swayRotMax: 0.10,
  swayPosAmount: 0.020,
  swayPosMax: 0.038,
  swayStiffness: 46,
  swayDamping: 9.2,
  swayAdsScale: 0.24,

  // --- movement lag: the weapon trails the body ---
  moveLagAmount: 0.0085,
  moveLagMax: 0.030,
  moveLagLerp: 7.0,

  // --- walk bob ---
  bobFreq: 2.05,
  bobAmp: 0.0165,
  bobRoll: 0.028,
  bobAdsScale: 0.13,

  // --- idle breathing ---
  breathAmp: 0.0026,
  breathFreq: 0.42,
  breathAdsScale: 0.45,

  // --- fire kick spring ---
  kickOmega: 34,
  kickZeta: 0.55,
  kickPivot: [0, -0.02, 0.17], // buttstock, in view space

  // --- ADS ---
  adsOvershoot: 1.18,

  // --- viewmodel light rig ---
  keyIntensity: 2.6,
  fillIntensity: 1.15,
  rimIntensity: 1.9,
  ambientIntensity: 0.55,

  // --- muzzle flash ---
  // Linear RGB, deliberately far above 1.0: the bloom pass thresholds at ~1.7.
  flashHdr: [7.0, 4.4, 2.0],
  flashTime: 0.042,
  flashScale: 0.085,
  lightIntensity: 22,
  lightDistance: 14,

  // --- misc ---
  inspectKey: 'KeyX',
};

/**
 * Per-weapon eye relief: camera-to-`aimPoint` distance while aiming.
 *
 * These were roughly 0.08–0.10 m, which is a real shooter's cheek weld and completely
 * wrong for a screen. Measured at 1080p, the VK-416's rear lens sat 79 mm from the
 * eye and its housing subtended 23.5 degrees — 45% of screen height — and the SR-12's
 * scope body covered 64%. The optic became the frame rather than a thing inside it.
 *
 * A modern shooter's sight picture is 30–36% of screen height with a generous field of
 * view around the ring, which is what these distances now solve for. Note that pushing
 * the weapon away is safe for aim: the ADS pose is derived from `aimPoint`, so the
 * sight stays on the camera axis at any distance (measured error stays under 4 px).
 *
 * The twenty-three roster weapons carry their own eye relief in their spec, because
 * the number is solved against that weapon's ocular radius: glass subtense is
 * `2 * atan(scale * rOcular / distance)`, and the whole roster is held to 12.5–19
 * degrees (24–39% of screen height). A weapon whose eye relief lives anywhere but next
 * to the optic that determines it drifts out of that band the moment the optic
 * changes, which is what `ADS_BY_MODEL` exists to prevent.
 */
const ADS_DISTANCE = {
  ar: 0.139,
  smg: 0.126,
  dmr: 0.150,
  shotgun: 0.126,
  lmg: 0.132,
  sniper: 0.150,
  ...ADS_BY_MODEL,
};

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _e = new THREE.Euler();
const _m = new THREE.Matrix4();

export class ViewModel {
  constructor(ctx, player) {
    this.ctx = ctx;
    this.player = player;
    this.events = ctx.events;

    this.root = new THREE.Group();
    this.kick = new THREE.Group();
    this.hold = new THREE.Group();
    this.root.add(this.kick);
    this.kick.add(this.hold);
    ctx.viewScene.add(this.root);

    this.kick.position.fromArray(TUNING.kickPivot);
    this.hold.position.fromArray(TUNING.kickPivot).negate();
    this.hold.scale.setScalar(TUNING.modelScale);

    this.models = new Map();
    this.model = null;
    this.weapon = null;

    /* --------------------------- animation state --------------------------- */
    this.adsRaw = 0; // 0..1 linear
    this.adsT = 0; // eased
    this.pose = { pos: new THREE.Vector3(), rot: new THREE.Vector3() };
    this.pose.pos.fromArray(TUNING.poses.hip.pos);
    this.pose.rot.fromArray(TUNING.poses.hip.rot);
    // Animation offsets are kept apart from the damped pose so a reload's tilt can
    // never feed back into the pose spring and drift.
    this.ao = { pos: new THREE.Vector3(), rot: new THREE.Vector3() };

    this.swayRot = new THREE.Vector2();
    this.swayRotVel = new THREE.Vector2();
    this.swayPos = new THREE.Vector2();
    this.swayPosVel = new THREE.Vector2();
    this.moveLag = new THREE.Vector3();

    this.bobPhase = 0;
    this.bobWeight = 0;

    this.kickVal = { back: 0, up: 0, roll: 0, side: 0 };
    this.kickVel = { back: 0, up: 0, roll: 0, side: 0 };
    this.kickOmega = TUNING.kickOmega;
    this.kickZeta = TUNING.kickZeta;

    this.boltT = 0;
    this.boltDur = 0.06;
    this.pumpT = -1;
    this.pumpDur = 0.5;
    this.triggerT = 0;
    // Manually-worked bolt (the sniper). -1 = idle, 0..1 = one cycle. Deliberately
    // *not* an `anim`: an anim sets `blockAds`, and a bolt gun that dropped out of
    // the scope after every shot would be unusable.
    this.boltCycleT = -1;
    this.boltCycleDur = 1.0;

    this.anim = null; // { type, t, dur, empty, ... }
    this.blockAds = false;

    this._prevYaw = player?.yaw ?? 0;
    this._prevPitch = player?.pitch ?? 0;
    this._props = [];
    this._elapsed = 0;

    this._buildFlash();
    this._buildLights();

    ctx.engine.registerUpdate((dt, t) => this.update(dt, t), 'fx');
  }

  /* ------------------------------------------------------------------------ */
  /* weapon management                                                        */
  /* ------------------------------------------------------------------------ */

  /** Build (once) and cache a weapon's model. */
  getModel(id) {
    if (!this.models.has(id)) {
      const m = buildWeaponModel(id, this.ctx);
      m.root.visible = false;
      this.hold.add(m.root);
      this.models.set(id, m);
    }
    return this.models.get(id);
  }

  setWeapon(weapon) {
    if (this.model) this.model.root.visible = false;
    this.weapon = weapon;
    this.model = this.getModel(weapon.model ?? weapon.id);
    this.model.root.visible = true;

    const k = weapon.viewKick;
    this.kickOmega = TUNING.kickOmega * (k.snap / 60);
    this.kickZeta = THREE.MathUtils.clamp(k.damp / 25, 0.2, 1.1);
    this.boltDur = 0.055 + (weapon.boltTravel ?? 0.03) * 0.6;
    this.boltCycleT = -1;

    // Park the flash at this weapon's muzzle.
    this.model.root.add(this.flash);
    this.flash.position.copy(this.model.muzzlePoint);
    this.flash.visible = false;

    this._resetParts();
    return this.model;
  }

  /**
   * Build and compile every weapon up front.
   *
   * Two hitches hide here and both only show up in play, never in a profiler run:
   * models are otherwise built on first switch (a few thousand triangles plus a
   * shader compile, mid-fight), and `renderer.compile` skips invisible objects — so
   * the muzzle flash, which is hidden until the first shot, would compile its shader
   * on the frame you first pull the trigger. Everything is made visible for one
   * compile call and then put back.
   */
  warmup(ids) {
    const hidden = [];
    for (const id of ids) {
      const m = this.getModel(id);
      if (!m.root.visible) {
        m.root.visible = true;
        hidden.push(m.root);
      }
      // The discarded magazine is a separate object in the view scene, so it needs
      // its own program. Build it once here and reuse it rather than allocating a
      // mesh (and compiling a shader) in the middle of the first reload.
      if (!m.magProp && m.parts.magGeom) {
        const prop = new THREE.Mesh(m.parts.magGeom, m.parts.magMat);
        prop.frustumCulled = false;
        prop.renderOrder = 2;
        prop.visible = true;
        this.ctx.viewScene.add(prop);
        m.magProp = prop;
        hidden.push(prop);
      }
    }
    const flashWasHidden = this.flash && !this.flash.visible;
    if (flashWasHidden) this.flash.visible = true;
    const propWasHidden = [];
    for (const m of this.models.values()) {
      m.root.traverse((o) => {
        if (o.isMesh && !o.visible) {
          o.visible = true;
          propWasHidden.push(o);
        }
      });
    }

    try {
      this.ctx.renderer?.compile?.(this.ctx.viewScene, this.ctx.viewCamera);
    } catch (err) {
      console.warn('[viewmodel] warmup compile failed', err);
    }

    for (const o of propWasHidden) o.visible = false;
    if (flashWasHidden) this.flash.visible = false;
    for (const r of hidden) r.visible = false;
    if (this.model) this.model.root.visible = true;
  }

  _resetParts() {
    const p = this.model?.parts;
    if (!p) return;
    if (p.bolt) {
      p.bolt.position.z = 0;
      // A rotating bolt (the sniper's) has to be put back down as well, or a switch
      // mid-cycle leaves the handle standing up for the rest of the match.
      p.bolt.rotation.z = 0;
    }
    if (p.charging) p.charging.position.z = 0;
    if (p.trigger) p.trigger.rotation.x = 0;
    if (p.magazine) {
      p.magazine.visible = true;
      p.magazine.position.copy(this.model.magOffset);
      p.magazine.rotation.set(0, 0, 0);
    }
    if (p.pump && this.pumpT < 0) p.pump.position.z = -0.230;
    if (p.leftHandMount) {
      p.leftHandMount.position.set(0, 0, 0);
      p.leftHandMount.rotation.set(0, 0, 0);
      p.leftHandMount.visible = true;
    }
    if (p.rightHandMount) {
      p.rightHandMount.position.set(0, 0, 0);
      p.rightHandMount.rotation.set(0, 0, 0);
    }
    if (p.loadShell) p.loadShell.visible = false;
  }

  /* ------------------------------------------------------------------------ */
  /* triggers from WeaponSystem                                               */
  /* ------------------------------------------------------------------------ */

  /** Called on every shot. Purely additive, so it layers over any active pose. */
  onFire(weapon) {
    const k = weapon.viewKick;
    const j = () => (Math.random() * 2 - 1);
    // Impulse into the spring: peak displacement ~= v0 / omega.
    const ads = 1 - this.adsT * 0.42; // aiming braces the weapon
    this.kickVel.back += k.back * this.kickOmega * ads * (0.88 + Math.random() * 0.24);
    this.kickVel.up += k.up * this.kickOmega * ads * (0.85 + Math.random() * 0.30);
    this.kickVel.roll += (k.roll * this.kickOmega * ads) * (0.4 + j() * 0.8);
    this.kickVel.side += k.side * this.kickOmega * ads * j();

    this.boltT = 1;
    this.triggerT = 1;
    this._flashOn(weapon);
    // Pump and bolt guns eject on the *cycle*, not on the shot — the brass leaving
    // the port at the same instant as the muzzle flash is the single clearest tell
    // that a manually-worked action was animated as an automatic one.
    if (weapon.fireMode !== 'pump' && weapon.fireMode !== 'bolt') this.ejectShell(weapon);
  }

  /** Pump-action cycle: the forend travels back and forward, ejecting on the way. */
  playPump(weapon) {
    this.pumpT = 0;
    this.pumpDur = weapon.pumpTime ?? 0.55;
    this._pumpEjected = false;
  }

  /**
   * Bolt cycle: lift, pull, eject, push, lock. Four beats and a pause, which is the
   * rhythm the whole weapon is built around — it is what the player is waiting
   * through after every shot, so it has to be worth watching.
   */
  playBoltCycle(weapon) {
    this.boltCycleT = 0;
    this.boltCycleDur = weapon.boltCycle ?? 1.0;
    this._boltEjected = false;
    this._boltLifted = false;
  }

  playReload(weapon, empty) {
    const r = weapon.reload;
    const dur = empty ? r.empty : r.tactical;
    this.anim = {
      type: weapon.shellByShell ? 'loadShell' : 'reload',
      t: 0,
      dur,
      empty,
      weapon,
      dropped: false,
      inserted: false,
      released: false,
    };
    this.blockAds = true;
  }

  playReloadStage(kind, weapon) {
    // Shell-by-shell start/end flourishes.
    this.anim = { type: kind, t: 0, dur: kind === 'shellStart' ? weapon.reload.start : weapon.reload.end, weapon };
    this.blockAds = true;
  }

  playSwitch(weapon, dir, dur) {
    this.anim = { type: dir < 0 ? 'holster' : 'draw', t: 0, dur, weapon };
    this.blockAds = true;
  }

  playInspect(weapon) {
    if (this.anim) return;
    this.anim = { type: 'inspect', t: 0, dur: weapon.ergonomics.inspect, weapon };
  }

  cancelAnim() {
    this.anim = null;
    this.blockAds = false;
    // A switch or a reload abandons a half-worked bolt; the state machine will not
    // let the weapon fire until it is idle again, and `_resetParts` puts the handle
    // back down.
    this.boltCycleT = -1;
    this._resetParts();
  }

  get busy() {
    return this.anim != null;
  }

  /* ------------------------------------------------------------------------ */
  /* per-frame                                                                */
  /* ------------------------------------------------------------------------ */

  update(dt, elapsed) {
    this._elapsed = elapsed;
    if (!this.model) return;
    const p = this.player;

    this._syncEnvironment();
    this._updateAds(dt);
    this._updatePose(dt);
    this._updateSway(dt, elapsed);
    this._updateKick(dt);
    this._updateParts(dt);
    this._updateAnim(dt);
    this._updateFlash(dt);
    this._updateProps(dt);

    /* ------------------------------ compose ------------------------------- */
    // Pose (already blended toward the active stance / ADS solution) plus the
    // additive sway and bob layers.
    const adsSway = THREE.MathUtils.lerp(1, TUNING.swayAdsScale, this.adsT);

    const speed = p ? p.speed : 0;
    const grounded = p ? p.grounded : true;
    const moving = grounded && speed > 0.6 && p.state !== 1;
    const speedRatio = THREE.MathUtils.clamp(speed / 6.6, 0, 1.3);
    this.bobWeight = THREE.MathUtils.damp(this.bobWeight, moving ? 1 : 0, 7, dt);
    if (moving) this.bobPhase += dt * TUNING.bobFreq * Math.PI * 2 * (0.62 + speedRatio * 0.95);
    const bobAmt =
      this.bobWeight * speedRatio * THREE.MathUtils.lerp(1, TUNING.bobAdsScale, this.adsT) * TUNING.bobAmp;
    const bobX = Math.sin(this.bobPhase) * bobAmt;
    const bobY = Math.sin(this.bobPhase * 2) * bobAmt * 0.55;
    const bobRoll = Math.sin(this.bobPhase) * TUNING.bobRoll * this.bobWeight * speedRatio * adsSway;

    // Breathing only reads when nearly still, which is when the player notices it.
    const still = 1 - THREE.MathUtils.clamp(speed / 1.4, 0, 1);
    const bAmp = TUNING.breathAmp * still * THREE.MathUtils.lerp(1, TUNING.breathAdsScale, this.adsT);
    const breathX = Math.sin(elapsed * TUNING.breathFreq * Math.PI * 2) * bAmp;
    const breathY = Math.sin(elapsed * TUNING.breathFreq * Math.PI * 2 * 1.6 + 1.1) * bAmp * 0.7;

    this.root.position.set(
      this.pose.pos.x + this.ao.pos.x + (this.swayPos.x + bobX + breathX + this.moveLag.x) * adsSway,
      this.pose.pos.y + this.ao.pos.y + (this.swayPos.y + bobY + breathY + this.moveLag.y) * adsSway,
      this.pose.pos.z + this.ao.pos.z + this.moveLag.z * adsSway
    );

    _e.set(
      this.pose.rot.x + this.ao.rot.x + this.swayRot.y * adsSway,
      this.pose.rot.y + this.ao.rot.y + this.swayRot.x * adsSway,
      this.pose.rot.z + this.ao.rot.z + bobRoll,
      'YXZ'
    );
    this.root.quaternion.setFromEuler(_e);

    /* ------------------------------- kick --------------------------------- */
    this.kick.position.set(
      TUNING.kickPivot[0] + this.kickVal.side,
      TUNING.kickPivot[1],
      TUNING.kickPivot[2] + this.kickVal.back
    );
    _e.set(this.kickVal.up, 0, this.kickVal.roll, 'YXZ');
    this.kick.quaternion.setFromEuler(_e);

    // Needs the finished transform for this frame, so it goes last.
    this._updateReticle();
  }

  /* ------------------------------ ADS solve ------------------------------- */

  _updateAds(dt) {
    const p = this.player;
    const want = p && p.ads && !this.blockAds && !(p.sprinting && this.adsRaw < 0.05) ? 1 : 0;
    const w = this.weapon;
    const rate = want ? 1 / (w?.ads.in ?? 0.25) : -1 / (w?.ads.out ?? 0.2);
    const prev = this.adsRaw;
    this.adsRaw = THREE.MathUtils.clamp(this.adsRaw + rate * dt, 0, 1);
    if (prev < 0.02 && this.adsRaw >= 0.02) this.events.emit('audio:play', { id: 'ads_in', volume: 0.4 });
    else if (prev > 0.98 && this.adsRaw <= 0.98) this.events.emit('audio:play', { id: 'ads_out', volume: 0.35 });

    // Ease with a slight overshoot on the way in: the sight arrives, keeps going a
    // few millimetres, and settles. That tiny bounce is the entire difference
    // between "the gun moved" and "the gun has weight".
    const t = this.adsRaw;
    if (want) {
      const c1 = TUNING.adsOvershoot - 1;
      const u = t - 1;
      this.adsT = 1 + (c1 + 1) * u * u * u + c1 * u * u;
    } else {
      this.adsT = t * t * (3 - 2 * t);
    }
  }

  /**
   * The ADS pose, derived from the model's aim point so the sight lands exactly on
   * the camera axis. No hand-tuned translation: `aimPoint + pos` must equal
   * `(0, 0, -eyeRelief)` in view space, so `pos = -aimPoint + (0, 0, -eyeRelief)`.
   */
  _adsPose(out) {
    const s = TUNING.modelScale;
    const a = this.model.aimPoint;
    const dist = ADS_DISTANCE[this.model.id] ?? 0.11;
    out.set(-a.x * s, -a.y * s, -dist - a.z * s);
    return out;
  }

  _updatePose(dt) {
    const p = this.player;
    const key = !p
      ? 'hip'
      : p.state === 1
      ? 'slide'
      : !p.grounded
      ? 'air'
      : p.tacSprinting
      ? 'tacSprint'
      : p.sprinting
      ? 'sprint'
      : p.crouching
      ? 'crouch'
      : 'hip';
    const base = TUNING.poses[key];

    // Blend the stance pose first, then cross-fade the whole thing to the ADS
    // solution. Sprint poses must never be snapped to or the transition reads as a
    // teleport, so the stance blend is damped and the ADS blend is curve-driven.
    _v.fromArray(base.pos);
    _v2.set(base.rot[0], base.rot[1], base.rot[2]);

    if (this.adsT > 0.001) {
      const t = this.adsT;
      this._adsPose(_tmpVec);
      _v.lerp(_tmpVec, t);
      _v2.multiplyScalar(1 - t);
    }

    const lerp = TUNING.poseLerp * (this.adsT > 0.001 ? 3.2 : 1);
    this.pose.pos.x = THREE.MathUtils.damp(this.pose.pos.x, _v.x, lerp, dt);
    this.pose.pos.y = THREE.MathUtils.damp(this.pose.pos.y, _v.y, lerp, dt);
    this.pose.pos.z = THREE.MathUtils.damp(this.pose.pos.z, _v.z, lerp, dt);
    this.pose.rot.x = THREE.MathUtils.damp(this.pose.rot.x, _v2.x, lerp, dt);
    this.pose.rot.y = THREE.MathUtils.damp(this.pose.rot.y, _v2.y, lerp, dt);
    this.pose.rot.z = THREE.MathUtils.damp(this.pose.rot.z, _v2.z, lerp, dt);
  }

  /* --------------------------------- sway --------------------------------- */

  _updateSway(dt, elapsed) {
    const p = this.player;
    if (!p) return;
    const erg = this.weapon?.ergonomics?.swayScale ?? 1;

    const dYaw = p.yaw - this._prevYaw;
    const dPitch = p.pitch - this._prevPitch;
    this._prevYaw = p.yaw;
    this._prevPitch = p.pitch;

    const invDt = 1 / Math.max(dt, 1e-4);
    const targetRotX = THREE.MathUtils.clamp(
      dYaw * invDt * TUNING.swayRotAmount * erg,
      -TUNING.swayRotMax,
      TUNING.swayRotMax
    );
    const targetRotY = THREE.MathUtils.clamp(
      -dPitch * invDt * TUNING.swayRotAmount * erg,
      -TUNING.swayRotMax,
      TUNING.swayRotMax
    );
    const targetPosX = THREE.MathUtils.clamp(
      -dYaw * invDt * TUNING.swayPosAmount * erg,
      -TUNING.swayPosMax,
      TUNING.swayPosMax
    );
    const targetPosY = THREE.MathUtils.clamp(
      dPitch * invDt * TUNING.swayPosAmount * erg,
      -TUNING.swayPosMax,
      TUNING.swayPosMax
    );

    spring2(this.swayRot, this.swayRotVel, targetRotX, targetRotY, TUNING.swayStiffness, TUNING.swayDamping, dt);
    spring2(this.swayPos, this.swayPosVel, targetPosX, targetPosY, TUNING.swayStiffness * 0.8, TUNING.swayDamping, dt);

    // Translation lag: the weapon trails the body when it accelerates.
    const yaw = p.yaw;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    const localZ = p.velocity.x * fx + p.velocity.z * fz;
    const localX = p.velocity.x * Math.cos(yaw) - p.velocity.z * Math.sin(yaw);
    const tx = THREE.MathUtils.clamp(-localX * TUNING.moveLagAmount, -TUNING.moveLagMax, TUNING.moveLagMax);
    const tz = THREE.MathUtils.clamp(localZ * TUNING.moveLagAmount, -TUNING.moveLagMax, TUNING.moveLagMax);
    const ty = THREE.MathUtils.clamp(-p.velocity.y * TUNING.moveLagAmount * 0.8, -TUNING.moveLagMax, TUNING.moveLagMax);
    this.moveLag.x = THREE.MathUtils.damp(this.moveLag.x, tx, TUNING.moveLagLerp, dt);
    this.moveLag.y = THREE.MathUtils.damp(this.moveLag.y, ty, TUNING.moveLagLerp, dt);
    this.moveLag.z = THREE.MathUtils.damp(this.moveLag.z, tz, TUNING.moveLagLerp, dt);
    void elapsed;
  }

  /* --------------------------------- kick --------------------------------- */

  _updateKick(dt) {
    const w = this.kickOmega;
    const z = this.kickZeta;
    for (const key of ['back', 'up', 'roll', 'side']) {
      const x = this.kickVal[key];
      const v = this.kickVel[key];
      // Semi-implicit Euler on a damped spring — stable at any dt we will ever see.
      const a = -w * w * x - 2 * z * w * v;
      const nv = v + a * dt;
      this.kickVal[key] = x + nv * dt;
      this.kickVel[key] = nv;
    }
  }

  /* ------------------------------ moving parts ---------------------------- */

  _updateParts(dt) {
    const parts = this.model.parts;
    const w = this.weapon;

    this._updateBoltCycle(dt, parts);

    // Bolt / slide reciprocation: 40% of the cycle rearward, 60% returning.
    // Suppressed while a manual bolt cycle owns the same part.
    if (parts.bolt && w && this.boltCycleT < 0) {
      if (this.boltT > 0) {
        this.boltT = Math.max(0, this.boltT - dt / this.boltDur);
        const u = 1 - this.boltT;
        const travel = u < 0.4 ? u / 0.4 : 1 - (u - 0.4) / 0.6;
        parts.bolt.position.z = travel * (w.boltTravel ?? 0.03);
      } else if (parts.bolt.position.z !== 0) {
        parts.bolt.position.z = 0;
      }
    }

    if (parts.trigger) {
      this.triggerT = Math.max(0, this.triggerT - dt * 9);
      parts.trigger.rotation.x = -this.triggerT * 0.32;
    }

    // Pump action: rearward, eject, forward, chamber.
    if (parts.pump && this.pumpT >= 0) {
      this.pumpT += dt / this.pumpDur;
      const u = Math.min(1, this.pumpT);
      const travel = u < 0.45 ? ease(u / 0.45) : 1 - ease((u - 0.45) / 0.55);
      parts.pump.position.z = -0.230 + travel * (this.weapon?.pumpTravel ?? 0.075);
      if (!this._pumpEjected && u > 0.38) {
        this._pumpEjected = true;
        this.ejectShell(this.weapon);
        this.events.emit('audio:play', { id: 'pump_back', volume: 0.7 });
      }
      if (u >= 1) {
        this.pumpT = -1;
        parts.pump.position.z = -0.230;
        this.events.emit('audio:play', { id: 'pump_forward', volume: 0.7 });
      }
    }

  }

  /**
   * One turn of a manually-worked bolt.
   *
   * Five beats over the weapon's own `boltCycle`: lift the handle, run it back (the
   * case leaves at the top of the stroke), a short dwell at the rear, push it home,
   * lock it down. The dwell is what makes the action read as *worked* rather than
   * as a fast reciprocation — without it the eye sees a semi-auto in slow motion.
   *
   * The bolt is a group whose pivot is the bore axis, so lifting is a rotation about
   * Z and cycling is a translation along it. Weapons whose `bolt` part is a plain
   * mesh are untouched: they never call this.
   */
  _updateBoltCycle(dt, parts) {
    if (this.boltCycleT < 0) return;
    const bolt = parts.bolt;
    this.boltCycleT += dt / Math.max(0.05, this.boltCycleDur);
    const u = Math.min(1, this.boltCycleT);
    const travel = this.weapon?.boltTravel ?? 0.05;

    // Handle lift: up over the first 15%, down over the last 15%.
    const lift = u < 0.15 ? ease(u / 0.15) : u > 0.85 ? 1 - ease((u - 0.85) / 0.15) : 1;
    // Rearward travel, held through the dwell between 0.45 and 0.58.
    const back =
      u < 0.15 ? 0 : u < 0.45 ? ease((u - 0.15) / 0.30) : u < 0.58 ? 1 : u < 0.85 ? 1 - ease((u - 0.58) / 0.27) : 0;

    if (bolt) {
      bolt.rotation.z = lift * 0.92;
      bolt.position.z = back * travel;
    }

    if (!this._boltLifted && u > 0.12) {
      this._boltLifted = true;
      this.events.emit('audio:play', { id: 'bolt_release', volume: 0.5, pitch: 1.18 });
    }
    if (!this._boltEjected && u > 0.40) {
      this._boltEjected = true;
      this.ejectShell(this.weapon);
      this.events.emit('audio:play', { id: 'reload_mag_out', volume: 0.45, pitch: 1.25 });
    }
    if (u >= 1) {
      this.boltCycleT = -1;
      if (bolt) {
        bolt.rotation.z = 0;
        bolt.position.z = 0;
      }
      this.events.emit('audio:play', { id: 'bolt_release', volume: 0.75, pitch: 0.92 });
    }
  }

  /**
   * The weapon's own motion during a bolt cycle: it rolls right and drops slightly
   * as the firing hand leaves the grip to work the handle, then comes back. Written
   * into the same additive offset the reload animations use, seeded here because the
   * cycle is not an `anim` and must not block ADS.
   */
  _boltCycleOffset(out) {
    out.pos.set(0, 0, 0);
    out.rot.set(0, 0, 0);
    if (this.boltCycleT < 0) return;
    const u = Math.min(1, this.boltCycleT);
    const s = bell(u, 0, 0.18, 0.62, 0.94);
    // Small, because the player is looking through a 5.7x scope while this happens
    // and anything larger reads as the whole world lurching.
    out.pos.x += -0.006 * s;
    out.pos.y += -0.004 * s;
    out.rot.z += -0.075 * s;
    out.rot.y += 0.030 * s;
  }

  /**
   * Collimated reticle.
   *
   * A reflex sight projects its dot to infinity, so the dot sits wherever the weapon
   * is *pointed*, not wherever the glass happens to be — move your head and the dot
   * stays on target while the tube slides around it. That is the entire reason a red
   * dot is faster than iron sights, and a dot glued to the glass throws it away: the
   * old code only nudged the dot by a fixed 2 mm at the hip and left it welded to the
   * lens while aiming, so it swam with every sway and bob input.
   *
   * Solving it needs the composed transform, so this runs *after* the pose, sway and
   * kick have been written for the frame rather than inside `_updateParts`.
   *
   * The clamp is the eyebox. Past it a real sight stops showing the dot rather than
   * painting it on the housing, so the dot is held inside the glass instead.
   */
  _updateReticle() {
    const m = this.model;
    const ret = m?.parts?.reticle;
    if (!ret || !m.reticleCollimated) return;
    if (!ret.userData.home) ret.userData.home = ret.position.clone();
    const home = ret.userData.home;

    ret.position.copy(home);
    this.root.updateMatrixWorld(true);
    // The view camera sits at the origin with no rotation, so viewScene world space
    // *is* view space and "on the camera axis" simply means x = y = 0.
    _v.copy(home).applyMatrix4(m.root.matrixWorld);

    let ox = -_v.x * this.adsT;
    let oy = -_v.y * this.adsT;
    const lim = m.reticleClear ?? 0.012;
    const d = Math.hypot(ox, oy);
    if (d > lim) {
      ox *= lim / d;
      oy *= lim / d;
    }
    _v.x += ox;
    _v.y += oy;

    _m.copy(m.root.matrixWorld).invert();
    ret.position.copy(_v.applyMatrix4(_m));
  }

  /* ----------------------------- animation states -------------------------- */

  _updateAnim(dt) {
    // Seeds the additive offset (zero unless a bolt is being worked); the animations
    // below add to it.
    this._boltCycleOffset(this.ao);
    const a = this.anim;
    if (!a) return;
    a.t += dt;
    const u = Math.min(1, a.t / a.dur);
    const parts = this.model.parts;

    switch (a.type) {
      case 'reload':
        this._animReload(a, u, parts);
        break;
      case 'loadShell':
      case 'shellStart':
      case 'shellEnd':
        this._animShell(a, u, parts);
        break;
      case 'holster':
        this._animHolster(u);
        break;
      case 'draw':
        this._animDraw(u);
        break;
      case 'inspect':
        this._animInspect(a, u, parts);
        break;
    }

    if (u >= 1) {
      this.anim = null;
      this.blockAds = false;
      this._resetParts();
    }
  }

  /**
   * Magazine reload. Three visible beats — the old magazine falls clear, the hand
   * leaves frame and returns with a fresh one, the magazine seats — plus a bolt
   * release only on an empty reload. The empty variant is longer *and* looks
   * different, which is the point: players should be able to tell at a glance
   * whether they got caught dry.
   */
  _animReload(a, u, parts) {
    const r = a.weapon.reload;
    const tOut = r.magOut;
    const tIn = a.empty ? r.magIn : r.magIn * 0.92;
    const tBolt = r.boltRelease;

    // --- weapon body: tilt in toward the chest, then back out ---
    const settle = bell(u, 0.0, 0.16, 0.86, 1.0);
    this.ao.pos.x += (-0.030 - 0.02 * (a.empty ? 1 : 0)) * settle;
    this.ao.pos.y += -0.034 * settle;
    this.ao.pos.z += 0.024 * settle;
    this.ao.rot.x += 0.16 * settle;
    this.ao.rot.y += 0.30 * settle;
    this.ao.rot.z += -0.42 * settle;

    // --- magazine ---
    if (parts.magazine) {
      if (u < tOut) {
        // Mag release pressed: it drops a couple of centimetres in the well first.
        const k = clamp01((u - (tOut - 0.10)) / 0.10);
        parts.magazine.position.y = this.model.magOffset.y - k * 0.020;
      } else if (u < tIn - 0.12) {
        if (!a.dropped) {
          a.dropped = true;
          parts.magazine.visible = false;
          this._spawnDroppedMag();
          this.events.emit('audio:play', { id: 'reload_mag_out', volume: 0.85 });
        }
      } else {
        // New magazine rises into the well and seats with a hard stop.
        const k = clamp01((u - (tIn - 0.12)) / 0.12);
        const e = ease(k);
        parts.magazine.visible = true;
        parts.magazine.position.y = this.model.magOffset.y - (1 - e) * 0.135;
        parts.magazine.position.z = this.model.magOffset.z + (1 - e) * 0.030;
        parts.magazine.rotation.x = (1 - e) * -0.42;
        if (k >= 1 && !a.inserted) {
          a.inserted = true;
          this.events.emit('audio:play', { id: 'reload_mag_in', volume: 0.9 });
          // A small kick sells the magazine slap.
          this.kickVel.up += 0.5;
          this.kickVel.back += 0.35;
        }
      }
    }

    // --- support hand: off-screen while it fetches the new magazine ---
    if (parts.leftHandMount) {
      const away = bell(u, tOut - 0.10, tOut + 0.06, tIn - 0.10, tIn + 0.02);
      parts.leftHandMount.position.y = -0.30 * away;
      parts.leftHandMount.position.z = 0.12 * away;
      parts.leftHandMount.rotation.x = -0.7 * away;
    }

    // --- bolt release on an empty reload ---
    if (a.empty && parts.bolt) {
      if (u < tBolt) {
        parts.bolt.position.z = a.weapon.boltTravel ?? 0.03; // locked back
      } else if (!a.released) {
        a.released = true;
        this.boltT = 1;
        this.kickVel.up += 0.9;
        this.kickVel.back += 0.6;
        this.events.emit('audio:play', { id: 'bolt_release', volume: 0.95 });
      }
      // The support hand slaps the bolt catch on the way past.
      if (parts.leftHandMount && u > tBolt - 0.10 && u < tBolt + 0.08) {
        const k = bell(u, tBolt - 0.10, tBolt - 0.04, tBolt + 0.02, tBolt + 0.08);
        parts.leftHandMount.position.y -= 0.05 * k;
        parts.leftHandMount.position.x = 0.06 * k;
      }
    }
  }

  /** Shell-by-shell shotgun loading: one shell per call. */
  _animShell(a, u, parts) {
    const settle = bell(u, 0, 0.2, 0.8, 1.0);
    this.ao.pos.x += -0.026 * settle;
    this.ao.pos.y += -0.030 * settle;
    this.ao.rot.z += -0.50 * settle;
    this.ao.rot.y += 0.22 * settle;

    if (a.type === 'loadShell') {
      if (parts.loadShell) {
        parts.loadShell.visible = u < 0.86;
        const k = ease(clamp01(u / 0.6));
        parts.loadShell.position.set(
          THREE.MathUtils.lerp(-0.09, 0.0, k),
          THREE.MathUtils.lerp(-0.16, -0.040, k),
          THREE.MathUtils.lerp(0.05, -0.018, k)
        );
        parts.loadShell.rotation.set(0, THREE.MathUtils.lerp(0.9, 0, k), 0);
      }
      if (parts.leftHandMount) {
        const away = bell(u, 0.0, 0.12, 0.52, 0.9);
        parts.leftHandMount.position.y = -0.24 * away;
        parts.leftHandMount.position.x = -0.06 * away;
      }
      if (parts.carrier) parts.carrier.rotation.x = -0.5 * bell(u, 0.5, 0.62, 0.72, 0.86);
      if (u > 0.62 && !a.clicked) {
        a.clicked = true;
        this.events.emit('audio:play', { id: 'shell_insert', volume: 0.85 });
        this.kickVel.up += 0.25;
      }
    } else if (a.type === 'shellEnd') {
      // Rack the action to chamber the first round.
      if (u > 0.15 && !a.racked) {
        a.racked = true;
        this.playPump(a.weapon);
      }
    }
  }

  _animHolster(u) {
    const e = ease(u);
    this.ao.pos.y -= 0.34 * e;
    this.ao.pos.z += 0.10 * e;
    this.ao.rot.x -= 1.05 * e;
    this.ao.rot.z += 0.35 * e;
  }

  _animDraw(u) {
    const e = 1 - ease(u);
    this.ao.pos.y -= 0.34 * e;
    this.ao.pos.z += 0.10 * e;
    this.ao.rot.x -= 1.05 * e;
    this.ao.rot.z += 0.35 * e;
  }

  /**
   * Inspect. Costs nothing, is never required, and is the kind of thing that reads
   * as a finished game rather than a prototype.
   */
  _animInspect(a, u, parts) {
    const s = bell(u, 0, 0.14, 0.80, 1.0);
    // Bring it in, roll it over to show the left side, tip the magazine into view.
    const phase = clamp01((u - 0.10) / 0.55);
    const roll = Math.sin(phase * Math.PI) * 1.55;
    const yaw = Math.sin(phase * Math.PI) * 0.55;
    this.ao.pos.x += (-0.052 + Math.sin(u * Math.PI * 2) * 0.008) * s;
    this.ao.pos.y += (-0.020 - Math.sin(phase * Math.PI) * 0.030) * s;
    this.ao.pos.z += 0.055 * s;
    this.ao.rot.x += (0.10 + Math.sin(phase * Math.PI * 2) * 0.14) * s;
    this.ao.rot.y += yaw * s;
    this.ao.rot.z += -roll * s;

    if (parts.charging && u > 0.68 && u < 0.86) {
      const k = Math.sin(((u - 0.68) / 0.18) * Math.PI);
      parts.charging.position.z = k * 0.05;
      if (parts.bolt) parts.bolt.position.z = k * (a.weapon.boltTravel ?? 0.03);
      if (!a.checked && u > 0.76) {
        a.checked = true;
        this.events.emit('audio:play', { id: 'bolt_release', volume: 0.4 });
      }
    }
  }

  /* --------------------------- viewmodel light rig ------------------------ */

  /**
   * The viewmodel scene is lit by the lighting module, which owns the key and fill
   * for it exactly as it does for the world — that is what keeps the weapon in the
   * same grade as everything else. Nothing is added here.
   *
   * The one thing this does is fail loudly rather than silently: a view scene with
   * no lights renders the weapon as a black silhouette, which during development
   * looked like the viewmodel had stopped drawing altogether and cost a long time to
   * diagnose. The fallback below only ever engages when there is genuinely no rig to
   * double up with, so it cannot blow the weapon out.
   */
  _buildLights() {
    const s = this.ctx.viewScene;
    let hasLight = false;
    s.traverse((o) => {
      if (o.isLight) hasLight = true;
    });
    if (hasLight) return;

    console.warn('[viewmodel] no lights in viewScene — installing a fallback rig so the weapon is not a silhouette');
    this.lights = new THREE.Group();
    const key = new THREE.DirectionalLight(0xfff2df, TUNING.keyIntensity);
    key.position.set(-0.6, 0.85, 0.5);
    this.lights.add(key, key.target);
    const fill = new THREE.DirectionalLight(0x8fb4d8, TUNING.fillIntensity);
    fill.position.set(0.85, -0.4, 0.35);
    this.lights.add(fill, fill.target);
    this.lights.add(new THREE.HemisphereLight(0xbcd4ee, 0x2a241c, TUNING.ambientIntensity));
    s.add(this.lights);
  }

  /** Only fills in an environment map if nothing else has provided one. */
  _syncEnvironment() {
    const view = this.ctx.viewScene;
    if (view.environment) return;
    const world = this.ctx.scene;
    if (world.environment) {
      view.environment = world.environment;
      view.environmentIntensity = world.environmentIntensity ?? 1;
    }
  }

  /* ------------------------------ muzzle flash ---------------------------- */

  _buildFlash() {
    const tex = makeFlashTexture();
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    // The post stack's bloom threshold sits above 1.0, so an LDR flash never blooms
    // and reads as a grey smear pasted over the barrel. Driving the colour well into
    // HDR is what makes it behave like an actual light source rather than a decal.
    mat.color.setRGB(TUNING.flashHdr[0], TUNING.flashHdr[1], TUNING.flashHdr[2]);
    this.flashMat = mat;
    this.flash = new THREE.Group();
    // A billboard plane in view space is automatically camera-facing, because the
    // view camera never rotates — one of the quiet benefits of a separate view scene.
    const front = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this.flash.add(front);
    for (let i = 0; i < 2; i++) {
      const q = new THREE.Mesh(new THREE.PlaneGeometry(1, 0.55), mat);
      q.rotation.set(i === 0 ? Math.PI / 2 : 0, 0, i === 0 ? 0 : Math.PI / 2);
      q.position.z = -0.28;
      q.scale.set(1.5, 1, 1);
      this.flash.add(q);
    }
    this.flash.renderOrder = 50;
    this.flash.visible = false;
    this.flash.traverse((o) => (o.frustumCulled = false));
    this.flashT = 0;
  }

  _flashOn(weapon) {
    this.flashT = TUNING.flashTime;
    const s = TUNING.flashScale * (weapon.pellets > 1 ? 1.7 : 1) * (0.82 + Math.random() * 0.4);
    this.flash.scale.setScalar(s);
    this.flash.rotation.z = Math.random() * Math.PI * 2;
    this.flash.visible = true;
    this.flashMat.opacity = 1;

    // World-space transform for the FX layer and the dynamic light.
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    this.root.updateMatrixWorld(true);
    _m.multiplyMatrices(cam.matrixWorld, this.flash.matrixWorld);
    const worldPos = new THREE.Vector3().setFromMatrixPosition(_m);

    this.events.emit('fx:muzzle', {
      matrix: this.flash.matrixWorld.clone(), // view space (the viewmodel's own scene)
      worldMatrix: _m.clone(),
      position: worldPos,
      scale: s,
      weapon: weapon.id,
    });

    const light = this.ctx.lighting?.addPointLight;
    if (typeof light === 'function') {
      try {
        this.ctx.lighting.addPointLight(worldPos, 0xffd39a, TUNING.lightIntensity * s * 12, TUNING.lightDistance, 0.06);
      } catch (err) {
        console.warn('[viewmodel] addPointLight failed', err);
      }
    }
  }

  _updateFlash(dt) {
    if (this.flashT <= 0) return;
    this.flashT -= dt;
    if (this.flashT <= 0) {
      this.flash.visible = false;
      return;
    }
    const k = this.flashT / TUNING.flashTime;
    this.flashMat.opacity = Math.pow(k, 0.6);
    this.flash.scale.setScalar(this.flash.scale.x * (1 + dt * 3.5));
  }

  /**
   * World-space position of the visible muzzle. Tracers drawn from the camera origin
   * look wrong at close range because they start behind the gun; starting them at the
   * muzzle the player can actually see is the difference between a tracer that reads
   * as a bullet and one that reads as a laser pointer.
   */
  worldMuzzle(fallback) {
    if (!this.model) return fallback ? fallback.clone() : new THREE.Vector3();
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    this.root.updateMatrixWorld(true);
    const out = new THREE.Vector3()
      .copy(this.model.muzzlePoint)
      .applyMatrix4(this.model.root.matrixWorld)
      .applyMatrix4(cam.matrixWorld);
    return out;
  }

  /* ----------------------------- shell ejection --------------------------- */

  ejectShell(weapon) {
    if (!this.model?.ejectPoint) return;
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    this.root.updateMatrixWorld(true);

    _v.copy(this.model.ejectPoint).applyMatrix4(this.model.root.matrixWorld); // view space
    _v.applyMatrix4(cam.matrixWorld); // world space

    const sh = weapon.shell ?? { speed: 3, size: 1 };
    _v2.copy(this.model.ejectDir ?? new THREE.Vector3(1, 0.3, 0.3)).normalize();
    _v2.transformDirection(this.model.root.matrixWorld);
    _v2.transformDirection(cam.matrixWorld);
    _v2.multiplyScalar(sh.speed * (0.82 + Math.random() * 0.36));
    // Inherit the player's own motion or the brass appears to hang in the air.
    if (this.player) _v2.add(this.player.velocity);

    this.events.emit('fx:shell', {
      position: _v.clone(),
      velocity: _v2.clone(),
      size: sh.size,
      weapon: weapon.id,
    });
  }

  /* -------------------------- dropped magazine prop ------------------------ */

  _spawnDroppedMag() {
    const parts = this.model.parts;
    if (!parts.magGeom) return;
    // Reuse the pre-warmed prop; a fresh mesh here would compile on the first reload.
    const m = this.model.magProp;
    if (!m) return;
    if (this._props.some((p) => p.mesh === m)) return; // already falling
    m.visible = true;
    this.root.updateMatrixWorld(true);
    // Hand it off from the weapon's frame into the view frame so it keeps falling
    // while the weapon carries on animating.
    _v.copy(this.model.magOffset).applyMatrix4(this.model.root.matrixWorld);
    m.position.copy(_v);
    m.rotation.set(0, 0, 0);
    this.model.root.getWorldQuaternion(_q);
    m.quaternion.copy(_q);
    this._props.push({
      mesh: m,
      vel: new THREE.Vector3(-0.08 + Math.random() * 0.05, -0.55, 0.16),
      spin: new THREE.Vector3(2.4 + Math.random(), 1.1, -0.8),
      life: 1.1,
    });
  }

  _updateProps(dt) {
    for (let i = this._props.length - 1; i >= 0; i--) {
      const p = this._props[i];
      p.life -= dt;
      p.vel.y -= 9.8 * dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.mesh.rotation.x += p.spin.x * dt;
      p.mesh.rotation.y += p.spin.y * dt;
      p.mesh.rotation.z += p.spin.z * dt;
      if (p.life <= 0 || p.mesh.position.y < -1.2) {
        p.mesh.visible = false;
        this._props.splice(i, 1);
      }
    }
  }

  /* ------------------------------------------------------------------------ */

  dispose() {
    for (const p of this._props) this.ctx.viewScene.remove(p.mesh);
    for (const m of this.models.values()) if (m.magProp) this.ctx.viewScene.remove(m.magProp);
    this._props.length = 0;
    this.ctx.viewScene.remove(this.root);
    if (this.lights) this.ctx.viewScene.remove(this.lights);
    for (const m of this.models.values()) {
      m.root.traverse((o) => o.geometry?.dispose?.());
    }
    this.models.clear();
    this.flashMat?.map?.dispose();
    this.flashMat?.dispose();
  }
}

/* -------------------------------------------------------------------------- */
/* helpers                                                                     */
/* -------------------------------------------------------------------------- */

const _tmpVec = new THREE.Vector3();

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function ease(t) {
  t = clamp01(t);
  return t * t * (3 - 2 * t);
}

/** Trapezoid envelope: 0 before a, 1 between b and c, 0 after d, smoothed. */
function bell(t, a, b, c, d) {
  if (t <= a || t >= d) return 0;
  if (t < b) return ease((t - a) / Math.max(1e-5, b - a));
  if (t > c) return 1 - ease((t - c) / Math.max(1e-5, d - c));
  return 1;
}

/** Critically-ish damped spring on a Vector2 toward a target. */
function spring2(val, vel, tx, ty, k, c, dt) {
  vel.x += (-k * (val.x - tx) - c * vel.x) * dt;
  vel.y += (-k * (val.y - ty) - c * vel.y) * dt;
  val.x += vel.x * dt;
  val.y += vel.y * dt;
}

/**
 * Muzzle flash sprite: a hot core with four radiating spikes, generated once.
 * Procedural because the project ships no binary assets, and a gradient like this
 * is a handful of lines anyway.
 */
function makeFlashTexture(size = 64) {
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const r = Math.hypot(dx, dy);
      const a = Math.atan2(dy, dx);
      // Four-point star: spikes reach further than the core.
      const spike = Math.pow(Math.abs(Math.cos(a * 2)), 6) * 0.55;
      const reach = 0.42 + spike;
      let v = Math.max(0, 1 - r / reach);
      v = Math.pow(v, 2.2);
      const core = Math.max(0, 1 - r / 0.22);
      v = Math.min(1, v + core * core * 1.4);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = Math.min(255, 190 + core * 65);
      data[i + 2] = Math.min(255, 110 + core * 120);
      data[i + 3] = Math.min(255, v * 255);
    }
  }
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  t.colorSpace = THREE.SRGBColorSpace;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}
